import React from 'react';
import {
  ShieldCheck, Monitor, Smartphone, MapPin, Globe,
  Trash2, Loader2, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';

const TrustedDevicesTab = ({
  trustedDevices,
  removingDevice,
  onRemoveDevice,
  onClearAll
}) => (
  <div className="space-y-6" data-testid="trusted-devices-section">
    <Card className="border border-orange-100">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-orange-500" />
              Trusted Devices & Locations
            </CardTitle>
            <CardDescription className="mt-1">
              Devices and locations where you've logged in before.
              You won't receive security alerts for these trusted locations.
            </CardDescription>
          </div>
          {trustedDevices.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClearAll}
              className="border-red-200 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {trustedDevices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No trusted devices yet</p>
            <p className="text-sm mt-1">
              Devices will be added automatically when you log in from new locations.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {trustedDevices.map((device) => (
              <div
                key={device.id || `${device.location}_${device.device}`}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
                data-testid={`trusted-device-${device.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    {device.device?.toLowerCase().includes('mobile') ||
                     device.device?.toLowerCase().includes('iphone') ||
                     device.device?.toLowerCase().includes('android') ? (
                      <Smartphone className="w-5 h-5 text-gray-600" />
                    ) : (
                      <Monitor className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {device.device || 'Unknown Device'}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {device.location || 'Unknown Location'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {device.ip_prefix ? `${device.ip_prefix}.xxx` : 'IP hidden'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      First seen: {device.first_seen
                        ? new Date(device.first_seen).toLocaleDateString()
                        : 'Unknown'}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveDevice(device.id || `${device.location}_${device.device}`.replace(/\s/g, '_').toLowerCase())}
                  disabled={removingDevice === device.id}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  data-testid={`remove-device-${device.id}`}
                >
                  {removingDevice === device.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>

    {/* Info Card */}
    <Card className="border border-blue-100 bg-blue-50">
      <CardContent className="pt-6">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <AlertCircle className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h4 className="font-semibold text-blue-800">How Trusted Devices Work</h4>
            <ul className="text-sm text-blue-700 mt-2 space-y-1">
              <li>When you log in from a new location or device, we send you an email alert</li>
              <li>That location/device is then added to your trusted list</li>
              <li>Future logins from trusted locations won't trigger alerts</li>
              <li>Remove a device if you no longer use it or if it was compromised</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

export default TrustedDevicesTab;
